import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Heart, MapPin, DollarSign, TrendingUp, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';

type Listing = Database['public']['Tables']['listings']['Row'] & { price: number | null };
type FavoriteWithListing = {
  id: string;
  created_at: string;
  listing: Listing;
};

export default function Favorites() {
  const [favorites, setFavorites] = useState<FavoriteWithListing[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          id,
          created_at,
          listing_id
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data) {
        setFavorites([]);
        return;
      }

      // Fetch listings for each favorite
      const listingIds = data.map(fav => fav.listing_id);
      if (listingIds.length === 0) {
        setFavorites([]);
        return;
      }

      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('*')
        .in('id', listingIds);

      if (listingsError) throw listingsError;

      // Combine favorites with listings
      const favoritesWithListings = data
        .map(fav => {
          const listing = listings?.find(l => l.id === fav.listing_id);
          return listing ? {
            id: fav.id,
            created_at: fav.created_at,
            listing
          } : null;
        })
        .filter(Boolean) as FavoriteWithListing[];

      setFavorites(favoritesWithListings);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId: string) => {
    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('id', favoriteId);

      if (error) throw error;

      setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Failed to remove favorite');
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Price on Application';
    return `AUD $${price.toLocaleString()}`;
  };

  const truncateText = (text: string, length: number = 120) => {
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-muted-foreground" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Sign In Required</h2>
              <p className="text-muted-foreground mb-6">
                Please sign in to view your favorite businesses.
              </p>
              <div className="space-x-4">
                <Button asChild>
                  <Link to="/sign-in">Sign In</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/explore">Browse Businesses</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="sm" asChild>
            <Link to="/explore">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Browse
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Heart className="h-8 w-8 text-red-500 fill-current" />
              My Favorites
            </h1>
            <p className="text-muted-foreground">
              Your saved business opportunities
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-video">
                  <Skeleton className="w-full h-full" />
                </div>
                <CardHeader>
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No favorites yet</h3>
              <p className="text-muted-foreground mb-6">
                Start browsing businesses and save the ones you're interested in.
              </p>
              <div className="space-x-4">
                <Button asChild>
                  <Link to="/explore">Browse Businesses</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/swipe">Try Swipe Mode</Link>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 text-sm text-muted-foreground">
              {favorites.length} favorite{favorites.length !== 1 ? 's' : ''} saved
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((favorite) => (
                <Card 
                  key={favorite.id} 
                  className="group overflow-hidden hover:shadow-large transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="aspect-video overflow-hidden bg-muted relative">
                    {favorite.listing.images && favorite.listing.images.length > 0 ? (
                      <img
                        src={favorite.listing.images[0]}
                        alt={favorite.listing.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-card">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                            <DollarSign className="h-6 w-6 text-primary" />
                          </div>
                          <p className="text-sm text-muted-foreground">Business Opportunity</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Remove Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={() => removeFavorite(favorite.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {favorite.listing.sector}
                      </Badge>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary">
                          {formatPrice(favorite.listing.price)}
                        </p>
                      </div>
                    </div>
                    <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                      {favorite.listing.title}
                    </CardTitle>
                    {favorite.listing.teaser && (
                      <CardDescription className="text-sm line-clamp-2">
                        {truncateText(favorite.listing.teaser)}
                      </CardDescription>
                    )}
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="space-y-2 mb-4">
                      {favorite.listing.location_suburb && favorite.listing.location_state && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{favorite.listing.location_suburb}, {favorite.listing.location_state}</span>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {favorite.listing.revenue && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <DollarSign className="h-3 w-3" />
                            <span>Rev: AUD ${(favorite.listing.revenue / 1000)}k</span>
                          </div>
                        )}
                        {favorite.listing.ebitda && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <TrendingUp className="h-3 w-3" />
                            <span>EBITDA: AUD ${(favorite.listing.ebitda / 1000)}k</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Link to={`/business/${favorite.listing.id}`}>
                      <Button 
                        variant="outline" 
                        className="w-full group-hover:border-primary group-hover:text-primary transition-colors"
                        size="sm"
                      >
                        View Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}